import { useEffect, useState } from "react";

import { ClinicalImpression } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { getClinicalImpressionData } from "@/fhir/resource-helpers/clinical-impression";
import { getPractitionerName } from "@/utils/medplum";

export function useEditSignature(clinicalImpression: ClinicalImpression) {
  const medplum = useMedplum();
  const [editorName, setEditorName] = useState<string>("Unknown");
  const [editHistory, setEditHistory] = useState<Array<{ name: string; date: string }>>([]);
  const { lastUpdated, questionnaireResponseId } = getClinicalImpressionData(clinicalImpression);

  useEffect(() => {
    async function load() {
      try {
        // Fetch assessor name
        const assessorId = clinicalImpression.assessor?.reference?.split("/")[1];
        if (assessorId) {
          const [name] = await getPractitionerName(medplum, assessorId);
          setEditorName(name || "Unknown");
        }

        // Fetch questionnaire history
        if (questionnaireResponseId) {
          try {
            const { entry = [] } = await medplum.readHistory(
              "QuestionnaireResponse",
              questionnaireResponseId,
            );
            // Get the original creation date to filter out non-edit entries
            const originalCreationDate = clinicalImpression.date;

            const edits = entry
              .slice(1) // Skip the first entry (original creation)
              .map((e) => ({
                name: e.resource?.author?.display ?? "Unknown",
                date: e.resource?.meta?.lastUpdated ?? "",
              }))
              .filter(
                (e) =>
                  e.date &&
                  originalCreationDate &&
                  new Date(e.date) > new Date(originalCreationDate),
              )
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

            if (edits.length) {
              setEditHistory(edits);
            }
          } catch (err) {
            console.error("Failed to load questionnaire history", err);
          }
        }
      } catch (err) {
        console.error("Error fetching editor name:", err);
        setEditorName("Unknown");
      }
    }
    load();
  }, [clinicalImpression, medplum, questionnaireResponseId]);

  return { editorName, editHistory, lastUpdated };
}
